/**
 * L'entrée et la sortie d'un `<Overlay>` : le voile entre en fondu, la feuille monte du bas (une
 * boîte centrée, elle, entre en fondu avec son voile, sans bouger) ; à la fermeture, l'inverse, et
 * l'overlay ne se démonte qu'une fois la sortie finie.
 *
 * Une seule valeur, `progress`, de 0 (fermé) à 1 (ouvert), porte le voile et le panneau : les deux
 * bougent ensemble. Les durées sont celles du livre (`theme.motion`) : `standard` à l'ouverture,
 * `micro` à la sortie, comme `<Snackbar>`. En mouvement réduit, rien ne bouge : l'overlay est là, ou
 * n'est plus là, tout de suite.
 *
 * Sans Unistyles : le crochet reçoit les jetons et le réglage du système, pour se tester sous Jest.
 */
import { useEffect, useRef, useState } from "react"
import { Animated, Easing, Platform } from "react-native"

/** Ce que la présence lit des jetons de motion (`theme.motion`). */
export type OverlayMotion = {
    micro: number
    standard: number
    easing: readonly number[]
}

/** Le réglage d'une animation d'entrée (`opening`) ou de sortie, pour `Animated.timing`. */
export function overlayTiming(
    opening: boolean,
    reduceMotion: boolean,
    motion: OverlayMotion,
    os: string = Platform.OS
) {
    const [x1, y1, x2, y2] = motion.easing
    return {
        toValue: opening ? 1 : 0,
        duration: reduceMotion ? 0 : opening ? motion.standard : motion.micro,
        easing: Easing.bezier(x1, y1, x2, y2),
        // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
        useNativeDriver: os !== "web",
    }
}

/**
 * Ce que fait le panneau pendant que `progress` va de 0 à 1 : une feuille (`bottom`) monte du bas
 * de l'écran (`distance`, la hauteur de la fenêtre : elle part d'en dessous, quelle que soit sa
 * taille) ; une boîte centrée entre en fondu avec son voile. Pure géométrie, aucun jeton.
 */
export function overlayPanelMotion(placement: "center" | "bottom", progress: Animated.Value, distance: number) {
    if (placement === "center") return { opacity: progress }
    return { transform: [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [distance, 0] }) }] }
}

export type OverlayPresence = {
    /** L'overlay est dans l'arbre : ouvert, en train d'entrer ou de sortir. */
    mounted: boolean
    /** Il sort : il garde ce qu'il montrait et ne répond plus au toucher. */
    closing: boolean
    /** 0 fermé, 1 ouvert. */
    progress: Animated.Value
}

export function useOverlayPresence(visible: boolean, reduceMotion: boolean, motion: OverlayMotion): OverlayPresence {
    const [mounted, setMounted] = useState(visible)
    // Toujours de 0 : une feuille montée déjà ouverte entre quand même.
    const progress = useRef(new Animated.Value(0)).current
    const { micro, standard } = motion
    const [x1, y1, x2, y2] = motion.easing

    useEffect(() => {
        const tokens = { micro, standard, easing: [x1, y1, x2, y2] }
        if (visible) {
            setMounted(true)
            const enter = Animated.timing(progress, overlayTiming(true, reduceMotion, tokens))
            enter.start()
            return () => enter.stop()
        }
        if (reduceMotion) {
            progress.setValue(0)
            setMounted(false)
            return
        }
        const leave = Animated.timing(progress, overlayTiming(false, reduceMotion, tokens))
        // Une sortie interrompue (la feuille rouverte entre-temps) ne démonte rien.
        leave.start(({ finished }) => finished && setMounted(false))
        return () => leave.stop()
    }, [visible, reduceMotion, progress, micro, standard, x1, y1, x2, y2])

    return { mounted: mounted || visible, closing: !visible, progress }
}
